import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { slackService } from "@/services/slackService";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// LocalStorage key for OAuth communication (bypasses COOP restrictions)
const OAUTH_RESULT_KEY = 'slack_oauth_result';

const SlackCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      // Extract params immediately
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state'); // This contains userId or JSON {userId, source}
      
      // Log to localStorage for debugging (can be checked from main window)
      try {
        const debugInfo = {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          hasCode: !!code,
          hasError: !!error,
          hasState: !!state,
          isPopup: window.opener !== null,
        };
        localStorage.setItem('slack_callback_debug', JSON.stringify(debugInfo));
      } catch (e) {
        // Ignore if localStorage fails
      }

      // Check if this is a mobile OAuth callback (source=mobile in state)
      // If yes, redirect to deep link and return early (don't modify web flow)
      if (code && state) {
        try {
          const stateData = JSON.parse(state);
          if (stateData.source === 'mobile') {
            // Redirect to deep link for mobile app
            const deepLinkUrl = `step://slack/callback?code=${code}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
            window.location.href = deepLinkUrl;
            return;
          }
        } catch {
          // If state is not JSON (legacy: just userId string), continue with web flow
        }
      }

      // Helper to notify parent via localStorage (COOP-safe)
      const notifyViaStorage = (success: boolean, errorMsg?: string) => {
        try {
          console.log('[SlackCallback] Notifying via storage:', { success, errorMsg });
          localStorage.setItem(OAUTH_RESULT_KEY, JSON.stringify({
            type: 'slack-oauth-callback',
            success,
            error: errorMsg,
            timestamp: Date.now(),
          }));
          // Trigger storage event
          window.dispatchEvent(new StorageEvent('storage', {
            key: OAUTH_RESULT_KEY,
            newValue: JSON.stringify({
              type: 'slack-oauth-callback',
              success,
              error: errorMsg,
              timestamp: Date.now(),
            }),
          }));
        } catch (err) {
          console.error('[SlackCallback] Failed to notify via storage:', err);
        }
      };

      // Helper to notify parent via postMessage (if COOP allows)
      const notifyViaPostMessage = () => {
        try {
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'slack-oauth-callback',
                code,
                error,
                state,
              },
              window.location.origin
            );
            return true;
          }
        } catch {
          // COOP blocked access to window.opener
        }
        return false;
      };

      // Check if this is a popup that should communicate back
      const isPopup = window.opener !== null || window.history.length <= 2;

      if (isPopup) {
        if (error) {
          const errorMsg = error === 'access_denied' 
            ? 'You cancelled the Slack connection' 
            : `Failed to connect Slack: ${error}`;
          
          // Try postMessage first, then use localStorage as fallback
          notifyViaPostMessage();
          notifyViaStorage(false, errorMsg);
          
          // Try to close popup
          setTimeout(() => window.close(), 100);
          return;
        }

        if (code) {
          try {
            const redirectUri = `${window.location.origin}/slack/callback`;
            
            // IMPORTANT: Call backend immediately - don't wait
            const result = await slackService.connectSlack(code, redirectUri);
            
            // Notify parent of success
            notifyViaPostMessage();
            notifyViaStorage(true);
            
            // Update debug info
            try {
              localStorage.setItem('slack_callback_debug', JSON.stringify({
                ...JSON.parse(localStorage.getItem('slack_callback_debug') || '{}'),
                success: true,
                result: { id: result.id, teamId: result.teamId },
              }));
            } catch (e) {
              // Ignore
            }
            
            // Try to close popup
            setTimeout(() => window.close(), 100);
            return;
          } catch (err: any) {
            const errorMsg = err?.response?.data?.error?.message || err.message || 'Failed to connect Slack workspace';
            
            // Update debug info
            try {
              localStorage.setItem('slack_callback_debug', JSON.stringify({
                ...JSON.parse(localStorage.getItem('slack_callback_debug') || '{}'),
                error: true,
                errorMsg,
                errorDetails: {
                  message: err.message,
                  status: err.response?.status,
                },
              }));
            } catch (e) {
              // Ignore
            }
            
            notifyViaPostMessage();
            notifyViaStorage(false, errorMsg);
            setTimeout(() => window.close(), 100);
            return;
          }
        }
      }

      // Fallback: Direct navigation flow (if popup was blocked or opened directly)
      // OR if code exists but popup detection failed
      if (error) {
        setStatus('error');
        setErrorMessage(error === 'access_denied' 
          ? 'You cancelled the Slack connection' 
          : `Failed to connect Slack: ${error}`);
        return;
      }

      if (!code) {
        // No code and no error - might be direct navigation without params
        setStatus('error');
        setErrorMessage('No authorization code received. Please try connecting again.');
        return;
      }

      // If we have code but not in popup, handle as direct navigation
      try {
        const redirectUri = `${window.location.origin}/slack/callback`;
        
        // Send code to backend to exchange for tokens
        const result = await slackService.connectSlack(code, redirectUri);
        
        setStatus('success');
        toast.success('Slack workspace connected successfully!');
        
        // Redirect back to settings after a short delay
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 1500);
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err?.response?.data?.error?.message || err.message || 'Failed to connect Slack workspace');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Connecting your Slack workspace...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-center">Connection Failed</CardTitle>
            <CardDescription className="text-center">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/settings')} className="w-full">
              Return to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <p className="text-sm text-muted-foreground">Slack workspace connected! Redirecting...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SlackCallback;
